import { Direction, WindowStartBounds } from "./types";

type MouseLocation = {
	x: number;
	y: number;
};

const defaultMouseLocation: MouseLocation = {
	x: -1,
	y: -1,
};

const defaultWindowStartBounds = {
	left: -1,
	top: -1,
	bottom: -1,
	right: -1,
	x: -1,
	y: -1,
	width: -1,
	height: -1,
	resizeType: <const>"None",
};

type MouseOffset = {
	top: number;
	left: number;
};

const defaultMouseOffset = {
	top: -1,
	left: -1,
};

type Edge = null | "left" | "right" | "top" | "bottom" | "top-right" | "top-left" | "bottom-right" | "bottom-left";

type WindowLocation = {
	top: number;
	left: number;
	width: number;
	height: number;
	right?: number;
	bottom?: number;
	changeType?: number;
};

type WindowOject = Record<string, any>;
/**
 *AssimilationMovingWindow is a wrapper for windows that are moving. This just allows us to handle extra data needed while a window is being moved
 *
 * @export
 * @class AssimilationMovingWindow
 */
export default class AssimilationMovingWindow {
	mouseLocation: MouseLocation;

	startBounds: WindowStartBounds;

	mouseOffset: MouseOffset;

	edge: Edge = null;

	direction: Direction = "None";

	location: WindowLocation;

	window: WindowOject;

	constructor(windowObject: WindowOject) {
		this.window = windowObject;
		this.mouseLocation = defaultMouseLocation;
		this.mouseOffset = defaultMouseOffset;
		this.startBounds = defaultWindowStartBounds;
		this.location = windowObject.location;
	}

	/**
	 *This is the start position of a window before it moves
	 *
	 * @param {*} params
	 * @param {*} params.left - the window left
	 * @param {*} params.right - the window right
	 * @param {*} params.bottom - the window bottom
	 * @param {*} params.top - the window top
	 * @param {*} params.x - The mouse x position
	 * @param {*} params.y - The mouse y position
	 * @param {*} params.width - the window width
	 * @param {*} params.height - the window height
	 *
	 *
	 * @memberof AssimilationMovingWindow
	 */
	setStartBounds(params: WindowStartBounds) {
		if (params.resizeType) this.direction = params.resizeType;
		this.startBounds = {
			left: params.left,
			top: params.top,
			bottom: params.bottom,
			right: params.right,
			x: params.x,
			y: params.y,
			width: params.width || params.right - params.left,
			height: params.height || params.bottom - params.top,
			resizeType: params.resizeType || this.direction,
		};
		this.location = this.startBounds;
		this.setMouseLocation(params);
		this.setMouseOffset(this.startBounds);
	}

	/**
	 * This sets the current mouse position. We use this to calculate where the window is trying to move
	 * @param {*} params
	 * @param {*} params.x
	 * @param {*} params.y
	 * @memberof AssimilationMovingWindow
	 */
	setMouseLocation(params: WindowStartBounds): void {
		this.mouseLocation = {
			x: Number(params.x),
			y: Number(params.y),
		};
	}

	/**
	 *This calculates our mouse offset so we only need to watch the mouse position on moves. This occurs during start move
	 *
	 * @param {*} params
	 * @param {*} params.x
	 * @param {*} params.y
	 * @param {*} params.top
	 * @param {*} params.left
	 * @memberof AssimilationMovingWindow
	 */
	setMouseOffset(params: WindowStartBounds): void {
		this.mouseOffset = {
			left: params.x - params.left,
			top: params.y - params.top,
		};
	}

	/**
	 *This function calculates where the window is trying to move to. It also tells us what edge, if any, is being moved
	 *
	 * @param {*} newMouseLocation
	 * @param {*} newMouseLocation.x
	 * @param {*} newMouseLocation.y
	 * @returns
	 * @memberof AssimilationMovingWindow
	 */
	calculateWindowLocation(newMouseLocation: MouseLocation): WindowLocation {
		const mouseLocation: MouseLocation = {
			x: Math.round(Number(newMouseLocation.x)),
			y: Math.round(Number(newMouseLocation.y)),
		};

		if (!this.edge) {
			this.edge = this.getResizeEdge(this.startBounds, mouseLocation, this.direction);
		}

		// calculate the change in mouse position
		let { left, right = 0, top, bottom = 0 } = this.location;
		let height: number;
		let width: number;
		let changeType = 0;

		// if we just moving the window
		if (!this.edge) {
			left = mouseLocation.x - Number(this.mouseOffset.left);
			top = mouseLocation.y - Number(this.mouseOffset.top);
			bottom = Number(bottom) - (mouseLocation.y - this.mouseLocation.y);
			right = Number(right) - (mouseLocation.x - this.mouseLocation.x);
			height = Number(this.location.height);
			width = Number(this.location.width);
			// if we are resizing
		} else {
			changeType = 1;
			switch (this.edge) {
				case "left":
					left = mouseLocation.x;
					break;
				case "right":
					right = mouseLocation.x;
					break;
				case "top":
					top = mouseLocation.y;
					break;
				case "bottom":
					bottom = mouseLocation.y;
					break;
				case "top-left":
					left = mouseLocation.x;
					top = mouseLocation.y;
					break;
				case "top-right":
					right = mouseLocation.x;
					top = mouseLocation.y;
					break;
				case "bottom-left":
					left = mouseLocation.x;
					bottom = mouseLocation.y;
					break;
				case "bottom-right":
					right = mouseLocation.x;
					bottom = mouseLocation.y;
				default:
					break;
			}

			width = right - left;
			height = bottom - top;
		}

		const windowLocation: WindowLocation = this.location;
		windowLocation.changeType = changeType;
		windowLocation.left = Number(left);
		windowLocation.top = Number(top);
		windowLocation.bottom = Number(bottom);
		windowLocation.right = Number(right);
		windowLocation.height = Number(height);
		windowLocation.width = Number(width);

		this.mouseLocation = mouseLocation;
		this.location = windowLocation;

		return windowLocation;
	}

	/**
	 * We use this function to figure out what side of a window is being resized
	 *
	 * @param {*} bounds
	 * @param {*} mouse
	 * @param {*} direction
	 * @returns
	 * @memberof AssimilationMovingWindow
	 */
	getResizeEdge(bounds: WindowStartBounds, mouse: MouseLocation, direction: Direction): Edge {
		if (direction === "None") {
			return null;
		}
		let edge: Edge = null;
		switch (direction) {
			case "WE":
				if (Math.abs(mouse.x - bounds.left) < Math.abs(mouse.x - bounds.right)) {
					edge = "left";
				} else {
					edge = "right";
				}
				break;
			case "NS":
				if (Math.abs(mouse.y - bounds.bottom) < Math.abs(mouse.y - bounds.top)) {
					edge = "bottom";
				} else {
					edge = "top";
				}
				break;
			case "NESW":
				if (Math.abs(mouse.y - bounds.top) < Math.abs(mouse.y - bounds.bottom)) {
					edge = "top-right";
				} else {
					edge = "bottom-left";
				}
				break;
			case "NWSE":
				if (Math.abs(mouse.y - bounds.top) < Math.abs(mouse.y - bounds.bottom)) {
					edge = "top-left";
				} else {
					edge = "bottom-right";
				}
			default:
				break;
		}
		return edge;
	}
}
